// The renderer facade (PLAN §5.1). React owns the chrome; nothing outside
// src/client/ai/render touches the scene graph, and nothing inside it imports React.
//
// **It draws `src/api` facts and is told what changed** (PLAN-CLIENT-SPLIT
// days 3–4): `setGame` subscribes to the `Game`, every change arrives as a
// `TurnDelta` with the board's facts already built, and nothing in here asks
// the rules anything — a moved rook, a shoved queen, a threatened king and a
// square that is ground are all fields on what it was handed.

import {
  Color,
  DirectionalLight,
  HemisphereLight,
  type Object3D,
  Plane,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
  WebGLRenderer
} from "three";

import {
  type BoardFacts,
  type BoardHighlights,
  type Bounds,
  type CargoLeg,
  type CarrySpec as ApiCarrySpec,
  fileOf,
  type Game,
  type Leg,
  rankOf,
  type Side as ApiSide,
  square,
  type Square,
  type Status,
  type TurnDelta
} from "src/api";
import { forwardFacing, pieceColor, type Side } from "src/shared/board";
import { builtBelt, DIR_INDEX, packedPiece, PIECE_TYPE, STRUCTURE_KIND } from "./factNames";
import { BoardTiles, type HighlightKind, MARKER_OVER_BELT } from "./boardTiles";
import {
  type CameraPose,
  CameraRig,
  type Projection,
  START_PITCH,
  START_YAW,
  type Tilt
} from "./cameraRig";
import { fileToX, rankToZ, squareToWorld, worldToSquare } from "./coords";
import { frameShare, shouldFrame } from "./framing";
import { ASLEEP_FRAME, clockHealth, frameDue, nextCarry, stepClamp } from "./frameCap";

/**
 * How much wall clock the health average is taken over.
 *
 * Long enough that one expensive frame does not move it and short enough that
 * a board which has just become expensive is reported within a turn or two.
 */
const HEALTH_WINDOW = 2;
import { DirectionPicker } from "./directionPicker";
import { BELT_DECK, IndustryViews, pieceLift } from "./industryViews";
import {
  loadIngotTextures,
  loadKenneyModels,
  loadPieceGeometries,
  loadVariantGeometries,
  loadPiecePack,
  PACK_PAINT,
  PACK_YAW,
  PIECE_YAW,
  type PiecePack,
  loadProps
} from "./models";
import { Outlines } from "./outline";
import { PALETTE } from "./palette";
import { PieceViews } from "./pieceViews";
import {
  CARRY_FREE,
  CARRY_GRIP,
  CARRY_LIFT,
  CARRY_SNAP_LIFT,
  CarryMotion,
  type CarrySpec
} from "./carryView";
import type { SoundEvent, SoundListener } from "./soundEvents";

// `BoardHighlights` and `BeltGhost` are the contract's (`src/api/session.ts`):
// what the session asks the board to mark, in the contract's names.
export type { BoardHighlights, BeltGhost } from "src/api";

/**
 * Turn sensitivity. Yaw is negated so the board follows the cursor — dragging
 * right turns the near edge right, like pushing the board itself.
 */
const DRAG_YAW_PER_PIXEL = -0.008;

/**
 * How much one notch of the wheel changes the view, as a log ratio: about a
 * quarter, so four notches roughly doubles or halves what is on screen.
 *
 * Measured against the board rather than picked: a notch that moves less than
 * a square is a control you have to spin, and one that moves more than about a
 * third of the screen loses your place — you look up and cannot find the piece
 * you were reasoning about.
 */
const ZOOM_PER_NOTCH = 0.22;

/** The board's extent, as something two syncs can be compared by. */
const boundsKey = (b: Bounds): string =>
  `${b.minFile}:${b.maxFile}:${b.minRank}:${b.maxRank}`;

export class BoardRenderer {
  private renderer: WebGLRenderer;
  private scene = new Scene();
  private rig: CameraRig;
  private tiles = new BoardTiles();
  private picker = new DirectionPicker();
  private pieces?: PieceViews;
  private industry?: IndustryViews;
  private raycaster = new Raycaster();
  /** Scratch for the plane intersections, so picking allocates nothing. */
  private deckHit = new Vector3();
  private groundHit = new Vector3();
  /** Scratch for `onScreen`, which runs on every borrowed look. */
  private lookHit = new Vector3();
  private pointer = new Vector2();

  private frame = 0;
  /** When the last frame that actually drew was handed to us, in ms. */
  private lastTime = 0;
  /** When the last frame of *any* kind was, so a skipped one's time is not lost. */
  private rawTime = 0;
  /** Seconds accumulated toward the next drawn frame, under a cap. */
  private banked = 0;
  /** Animation time granted, and wall clock spent, over the recent window. */
  private healthAdvanced = 0;
  private healthElapsed = 0;
  /** Seconds between drawn frames under a cap. 0 is uncapped, which is the default. */
  private frameInterval = 0;
  private needsRender = true;
  /** What the last frame's `update` pass reported: is anything still moving? */
  private busy = false;
  /**
   * The factory state waiting for the pieces to finish moving.
   *
   * Held rather than applied, so the ore gets a beat of its own. Cleared by a
   * snap (a load or a replay), which must not queue behind an animation that
   * is not happening.
   */
  private pendingIndustry?: {
    facts: BoardFacts;
    /** What the tick said moved, so ingots animate instead of teleporting. */
    cargo?: readonly CargoLeg[];
  };
  private frameListeners = new Set<() => void>();
  /**
   * Who is listening for the board's own events. See `onSound`.
   *
   * **Nothing is emitted from the load-and-replay branch of `setState`**, and
   * that is the property the whole design leans on: a reconnect replays a room
   * log command by command and a resumed run lands a whole board at once, so a
   * channel keyed off state changes would play a game's worth of sound in a
   * second. These fire where the *picture* changes, and that branch is the one
   * that deliberately animates nothing.
   */
  private soundListeners = new Set<SoundListener>();
  /**
   * Squares this turn's move arrived on that were captures.
   *
   * `piece-landed` is raised by `PieceViews` the frame a model stops, and by
   * then the board has no way to say whether anything was taken there — the
   * victim is already gone. Recorded when `moved` is built, from the move
   * record, which is the only thing that knows.
   */
  private capturedAt = new Set<number>();
  /**
   * Ore the tick's dig pass brought up, held for the same beat.
   *
   * A count and no square: which heaps were worked is not in the report, and a
   * centroid of them would be a lie — so this is announced non-positionally.
   * The *sweep* is the other door and is not here, because it happens with the
   * move rather than with the factory.
   */
  private pendingMined = 0;
  /**
   * Whether the factory waits for the pieces, or resolves with them.
   *
   * On by default and that is the shipped reading of a turn: the move lands,
   * then the ore moves, so a turn is a sequence of things that happened rather
   * than one blur. Off is "Opponent plays fast" in the View menu — see
   * `ui/cameraPrefs.ts`, which is where the choice is made and remembered.
   *
   * A separate knob from `tempo`, because they are different complaints:
   * `tempo` is how *fast* somebody else's turn plays, this is whether it plays
   * as two beats or one. They compose.
   */
  private staging = true;
  /** Playback speed for the turn on screen. 1 while it is the player's. */
  private tempo = 1;
  /**
   * Playback speed asked for from outside, as a multiplier on every tempo.
   *
   * Two things multiply into it and neither is the crowd ramp: a transport's
   * speed (the watch transport) and the player's own animation
   * scale from the View menu. One number, because the driver's clock takes the
   * same one — see `GameShell`, which is the only place that knows both.
   */
  private paceScale = 1;
  private disposed = false;

  /**
   * Per-side camera viewpoints. A side's pose is only meaningful once it has
   * actually held the camera, so the second entry starts unclaimed and is
   * derived from the *other* side's pose at the first handover.
   *
   * Deriving it beats a fixed half-turn: if White happens to rotate to the
   * angle Black's default was hard-coded to, the handover would do nothing at
   * all and the board would silently stop changing hands.
   */
  private poses: CameraPose[] = [
    { yaw: START_YAW, pitch: START_PITCH, focusX: 0, focusZ: 0 },
    { yaw: START_YAW + Math.PI, pitch: START_PITCH, focusX: 0, focusZ: 0 }
  ];
  private claimed: boolean[] = [true, false];
  private viewpoint: Side = 0;
  private sides = 2;

  /** The board on screen, as the engine described it. See `setGame`. */
  private facts?: BoardFacts;
  private status?: Status;
  private game?: Game;
  private unsubscribe?: () => void;
  /**
   * The board extent the camera was last framed for.
   *
   * A string rather than the bounds object: it is only ever compared, and
   * comparing four numbers by hand is four chances to add a fifth field later
   * and forget one of them.
   */
  private framedExtent = "";
  /**
   * The ply this renderer opened on, or -1 before its first sync.
   *
   * **Not always zero, which is the whole reason it is a field.** A resumed run
   * is restored from a snapshot carrying its real move history, so it opens at
   * ply forty on a board several camps wide — and "frame while no move has
   * been played" is false from the first frame, so the camera sat at the
   * default zoom over wherever the origin happened to be. What that rule
   * actually means is *while the player has not taken the camera*, and a move
   * played **since this renderer opened** is what taking it looks like.
   */
  private openedPly = -1;
  /**
   * The player has moved the camera by hand since this renderer opened.
   *
   * The other half of "while the player has not taken the camera" above: a
   * move played is one way of taking it and a drag, a wheel notch or a turn is
   * the other. `setViewInset` reads it, because its opening re-fit ran on
   * every selection made before the first move and threw away the zoom and
   * the pan the player had just set — with "Move the camera for me" off as
   * well as on, since it is no borrowed look and never asked.
   */
  private cameraTaken = false;
  /** May a move's reaction borrow the view? See `setFollowMoves`. */
  private followMoves = true;
  /** The run's opening shot has been launched. See `flyKingIn`. */
  private flownIn = false;
  private highlights: BoardHighlights = {};
  /** True while a belt tool is up and the army is drawn back. */
  private faded = false;
  /**
   * The workshop the entity highlights are cut from, shared by the army and
   * the buildings — one resolution uniform, one resize. Built here rather than
   * with the views because `resize` runs in this constructor, well before the
   * models land.
   */
  private outlines = new Outlines();
  /** Who is wearing a rim, so a view built later still gets one. */
  private outlined: ReadonlyMap<number, number> = new Map();

  readonly ready: Promise<void>;

  constructor(private canvas: HTMLCanvasElement) {
    // **Transparent, so the board sits *in* the page rather than in a box.**
    //
    // The clear colour was opaque and the canvas owned its rectangle, which is
    // what made the board a panel with chrome around it. With alpha the page
    // paints the ground and the canvas contributes only what is on it, so the
    // board can be full-bleed behind the interface and the interface can float
    // over it — and, not incidentally, the canvas then never resizes when a
    // panel moves, which is the whole of the black-frame rule `layout.ts`
    // exists to keep.
    this.renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setClearColor(new Color(PALETTE.background), 0);
    this.renderer.shadowMap.enabled = true;

    this.rig = new CameraRig(1);
    this.scene.add(this.tiles.group);
    this.scene.add(this.picker.group);

    // Contact shadows are not decoration here: under an orthographic camera
    // they are what tells you which square a piece is actually on.
    const key = new DirectionalLight(0xfff6e8, 3.1);
    key.position.set(6, 12, 8);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.left = -7;
    key.shadow.camera.right = 7;
    key.shadow.camera.top = 7;
    key.shadow.camera.bottom = -7;
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 32;
    // The pieces are curved solids that both cast and receive, so they
    // self-shadow badly without a generous normal bias — the symptom is a
    // mottled grey wash over what should be flat ivory, which is easy to
    // misread as a material or colour-space problem.
    key.shadow.normalBias = 0.06;
    key.shadow.bias = -0.0003;
    this.scene.add(key);

    // Fill from the side the key does not reach, so dark pieces keep their
    // silhouette instead of going to a flat black mass.
    const fill = new DirectionalLight(0xdfe8ff, 0.55);
    fill.position.set(-7, 5, -6);
    this.scene.add(fill);

    // Warm sky, and kept low. A cool hemisphere at high intensity lights the
    // *tops* of everything, which at this pitch is most of what you see — it
    // washed the ivory pieces to a flat blue-grey and read as a colour-space
    // bug rather than as a lighting choice.
    this.scene.add(new HemisphereLight(0xfff4e2, PALETTE.boardEdge, 0.5));

    this.ready = Promise.all([
      loadPieceGeometries(),
      loadVariantGeometries(),
      loadKenneyModels(),
      loadProps(),
      loadIngotTextures()
    ]).then(
      ([geometries, variants, kenney, props, ingotTextures]) => {
        if (this.disposed) return;
        this.pieces = new PieceViews(geometries, this.outlines, variants);
        this.industry = new IndustryViews(kenney, props, geometries, this.outlines);
        this.industry.setIngotTextures(ingotTextures);
        // One authority on how far a square is from being seated, read by
        // everything that stands on one. Bound once — the function is stable,
        // only its answer changes.
        const ground = (square: number) => this.tiles.riseOf(square);
        this.pieces.setGround(ground);
        this.industry.setGround(ground);
        // A piece carries what it is holding. The arc belongs to the piece, so
        // the factory borrows it rather than easing a second one beside it.
        this.industry.setTravel((square, out) => this.pieces?.motionOffset(square, out) ?? null);
        // **The two hooks the sound channel hangs off.** Both are moments only
        // the views can see — the frame a model stops, and the frame the ovens
        // light — and both are enriched here, because whether a landing was a
        // take is a fact about the *move*, which `PieceViews` never sees.
        //
        // A capture is spent as it is read: the same square is landed on again
        // later in the game, by a quiet move, and would otherwise
        // inherit a take that happened turns ago.
        this.pieces.setOnLanded((square) => {
          const capture = this.capturedAt.delete(square);
          this.emitSound({ kind: "piece-landed", at: square, capture });
        });
        this.industry.setOnPhase((phase, cooking) => {
          if (phase === "cook") this.emitSound({ kind: "cook-started", squares: cooking });
        });
        // Both of these can be asked for before the models land — a tool is
        // switched, or a run is drawn, while the GLBs are still in flight —
        // and neither has anywhere to go until now.
        this.pieces.setFaded(this.faded);
        this.industry.setGhosts(this.highlights.beltPreview ?? []);
        this.pieces.setOutlines(this.outlined);
        this.industry.setOutlines(this.outlined);
        this.scene.add(this.pieces.group);
        this.scene.add(this.industry.group);
        if (this.facts) {
          this.syncPieces(this.facts, null);
          this.pieces.setLift((square) => this.liftAt(this.facts!, square));
          this.industry.sync(this.facts);
        }
        this.invalidate();
      }
    ).catch((error: unknown) => {
      // A model that did not arrive (a redeploy mid-boot) leaves the board
      // undrawn, not the page in error — the net smoke's restart caught it.
      console.warn("board models did not load", error);
    });

    this.resize();
    this.loop(0);
  }

  /**
   * The game to draw.
   *
   * **One subscription, and the renderer works nothing out.** Every change
   * arrives as a `TurnDelta` — a move with its legs, a tick, a free change, a
   * history step, a load — beside the board's facts, already built and
   * memoised on the viewed board. The played/ticked/free inference, the castle
   * rook, the shove and the drag, the change tint's fallback and "is this
   * square ground" all used to be re-derived in here from a `GameState`; they
   * are the engine's answers now (PLAN-CLIENT-SPLIT §2.2).
   *
   * A new game is a new board: it lands as a load, so nothing on screen
   * continues into it and nothing is announced.
   */
  setGame(game: Game): void {
    if (this.game === game) return;
    this.unsubscribe?.();
    this.game = game;
    this.unsubscribe = game.on("turn", (delta) =>
      this.sync(game.board.facts(), delta, game.board.status())
    );
    this.sync(game.board.facts(), { kind: "load" }, game.board.status());
  }

  /** How high a piece on this square sits: on a deck, and on a deck being pressed. */
  private liftAt(facts: BoardFacts, square: number): number {
    const at = facts.squares.get(square as Square);
    return pieceLift(builtBelt(at) !== undefined && at?.piece !== undefined, this.industry?.squashAt(square));
  }

  private sync(facts: BoardFacts, delta: TurnDelta, status: Status): void {
    this.facts = facts;
    this.status = status;

    // One pass over the facts for the ground: which squares are drawn as
    // floor.
    //
    // **A building's square gets a floor too**, though it is deliberately not
    // a cell — `cell` says `structure-floor`. The encoding is right: nothing
    // may move onto it or slide through it, but that is a rule about
    // *movement*, and drawing it as a hole in the board states something much
    // stronger. Without this a turret stood on a bare coloured pad in the
    // middle of the floor, which reads as a marker rather than as a thing
    // built on a square. Sorted, because two clients draw one board — the
    // facts arrive in square order.
    const cells: number[] = [];
    for (const [sq, at] of facts.squares) {
      if (at.cell !== "none") cells.push(sq);
    }

    const arriving = this.tiles.setCells(cells);
    // **Ground coming up, including the first board of a game** — which is not
    // an exception to make: every tile genuinely rises on that sync, and the
    // moment a game opens is a moment worth marking. Empty on the syncs that
    // change nothing about the board's extent, which is nearly all of them.
    if (arriving.length > 0) this.emitSound({ kind: "cells-rising", squares: arriving });
    // Markers clear whatever is standing on the square rather than hiding
    // under it. A belt deck buried them completely, and so does a scrap heap —
    // the square you most need to see you can move to is routinely the one
    // with a dead queen's nine scrap piled on it.
    //
    // Two callbacks, because the marker and its wash want different answers:
    // the ring floats clear, the tint stays on the ground. A wash hovering at
    // the height of a big heap reads as a panel rather than as lit ground.
    this.tiles.setLift(
      (square) => {
        const at = facts.squares.get(square as Square);
        return Math.max(builtBelt(at) ? MARKER_OVER_BELT : 0, heapMarkerHeight(at?.heap?.size ?? 0));
      },
      (square) => (builtBelt(facts.squares.get(square as Square)) ? MARKER_OVER_BELT : 0)
    );

    // How far the view may travel: the board's extent, less what is already on
    // screen. On an eight-by-eight the whole board fits, so this is zero and
    // panning does nothing — there is nowhere to pan *to*. It opens up exactly
    // as the board grows past the viewport.
    // **A run's extent is its level, not the region it has opened.** Regions
    // arrive as objectives complete, so at the moment a run starts the board is
    // the opening lobe alone and framing on it is a close-up of one corner of
    // the map. `facts.framed` is the engine's union of the board with the
    // ground the level has yet to reveal — the board itself for every
    // scenario, versus and hot-seat board — and `frameShare` caps it so the
    // board keeps half the frame.
    //
    // Fed to the *extent* rather than only to the framing call, because the rig
    // reads one rectangle for three things — where to centre, how far out the
    // fit may go, and how far the view may travel. Widening only the fit would
    // frame the level and then have the pan clamp pull the focus straight back
    // onto the revealed lobe.
    const extentBounds = frameShare(facts.bounds, facts.framed);
    this.setPanRegionFor(extentBounds);
    // **An opening frames itself; once you have played a move the camera is
    // yours.** The rule and its three cases are `shouldFrame`, out in a file
    // of its own because it cannot be tested in here and has been wrong twice.
    // What is here is the one piece of state it needs: the ply this renderer
    // opened on, which is zero for a new game and is not for a resumed run.
    if (this.openedPly < 0) this.openedPly = status.ply;
    const extent = boundsKey(extentBounds);
    if (shouldFrame(extent, this.framedExtent, status.ply, this.openedPly)) {
      // Centred on the board, sized to the level: see `frameNow`'s parameter.
      const b = facts.bounds;
      // A framed opening is a new opening: the camera is the game's again
      // until somebody takes it.
      this.cameraTaken = false;
      this.rig.frameNow({
        x: (fileToX(b.minFile) + fileToX(b.maxFile)) / 2,
        z: (rankToZ(b.minRank) + rankToZ(b.maxRank)) / 2
      });
    }
    this.framedExtent = extent;
    // **How fast to play this turn back**: the engine's pace for the side
    // that just moved, times the transport's speed, which is the other half of
    // one control: the driver deals a plan out at `STEP_MS / pace` and this
    // plays the board, so a speed reaching only one of them leaves the pieces
    // waiting for the clock or the clock landing on a board still moving.
    this.tempo = facts.tempo * this.paceScale;

    // **What kind of change this is, said by the engine.** A move animates its
    // legs; a tick is a turn spent without a move — a forge, a pass
    // — and it still resolves the factory, since a lane advances and a belt
    // carries whatever is standing on it; a free change (a belt laid, a policy
    // flipped) resolves no turn; a snap is a history step, an undo or a rewind;
    // a load is a replay, a reconnect or a new game.
    const played = delta.kind === "move";
    const tick = delta.kind === "move" || delta.kind === "tick" ? delta.tick : undefined;
    const resolved = played || delta.kind === "tick";
    // **What just changed, in one tint** (§29 U4). The engine's answer: a
    // played move's two squares, or the store's diff for every change no move
    // explains. **The tint does not fade** (designer, 2026-09-10), so an empty
    // list puts it out — First, a new game, a load — and a tick that carries
    // none says nothing at all and leaves it standing, which is what `pass`
    // wants.
    if (delta.kind === "load") this.tiles.setChanged([]);
    else if (delta.kind !== "free" && delta.changed) this.tiles.setChanged(delta.changed);
    this.syncPieces(facts, resolved ? delta : null);
    // The lift has to be set before the pieces are synced: a view created
    // this frame reads it when it is positioned.
    this.pieces?.setLift((square) => this.liftAt(facts, square));
    // The factory resolves *after* the piece lands, as its own beat.
    //
    // Both used to happen on the same frame, so a turn's whole industry — ore
    // stepping along a lane, a heap drawing down, a foundry's stock changing —
    // resolved underneath a piece that was still sliding across it, and the
    // eye had no chance to follow any of it. Staging them is the same argument
    // the piece animation itself won: a turn should be legible as a sequence
    // of things that happened, not a single instant that is already over.
    //
    // Only when a move is being animated. A load or a replay snaps, because
    // there is no move to sequence it behind.
    if (resolved) {
      const beat = { facts, cargo: tick?.cargo };
      // **A beat superseded before its release still happened.** The release
      // waits one frame for the pieces, and the solo driver routinely lands
      // its next command inside that window — measured in play: sounds held
      // for the beat were overwritten below and never heard. What the beat
      // still holds is said now, a frame early, rather than dropped.
      this.announceFactoryBeat();
      // **What the beat will be heard doing, held with the beat itself.** The
      // dig pass resolves when the heap is drawn going down — so on the staged
      // path it belongs to the release below, not to this frame.
      this.pendingMined = tick?.mined ?? 0;
      // **The sweep and the mint are heard now, with the move.** A king lifts
      // what it lands on as it arrives, and a forged piece is already standing
      // on its square this frame — `syncPieces` above has drawn it. The craft
      // was held for the beat once, and the delay read as the sound coming
      // late rather than as sequencing: the click is the moment.
      const swept = tick?.collected;
      if (swept) this.emitSound({ kind: "collected", at: swept.at, amount: swept.amount });
      for (const piece of tick?.minted ?? []) {
        this.emitSound({ kind: "minted", at: piece.by, type: PIECE_TYPE[piece.type] });
      }
      // A placed structure is a mint's twin: standing on the board this frame.
      for (const placed of tick?.placed ?? []) {
        this.emitSound({ kind: "structure-placed", at: placed.square, structure: STRUCTURE_KIND[placed.kind] });
      }
      if (this.staging) {
        this.pendingIndustry = beat;
      } else {
        // **"Opponent plays fast": the beat happens now, over the move.**
        this.pendingIndustry = undefined;
        this.industry?.sync(beat.facts, beat.cargo);
        this.announceFactoryBeat();
      }
      // **The guns fire now, over the move, rather than waiting for the
      // factory's beat.**
      //
      // They used to be stashed above and released with the cargo, on the
      // argument that a turret is part of the industry a turn resolves and
      // firing under a sliding piece is the blur the staging exists to undo.
      // That is right about ingots and wrong about gunfire: a shot is an
      // *interaction* between two squares, and holding it back plays the cause
      // and the effect a beat apart — the piece finishes its move, stops, and
      // is only then shot at. Concurrent, it reads as the thing that actually
      // happened.
      //
      // It is also the one part of the tick with somewhere of its own to be:
      // ingots share the lanes and hoppers the eye is already following, and a
      // tracer is over open board.
      if (tick?.shots.length) this.industry?.fire.start(tick.shots);

      // **A costume coming off, said out loud.** Here rather than staged
      // behind the move, and for the turret's reason turned around: the shot
      // is an interaction *between* two squares and plays with the move; this
      // is the move's own consequence, on the piece that just made it, so it
      // has to land the instant that piece arrives. Held back a beat it reads
      // as a piece stopping and then being told off.
      this.unmask(tick?.unmasked);
    } else {
      // A load, a replay or a snap: no move to sequence behind, and nothing to
      // animate either. Passing a tick here would replay one turn's worth of
      // ingot movement over a board that arrived all at once.
      //
      // **And nothing is announced from here**, which is the property the
      // sound channel rests on: a reconnect replays a log command by command
      // and a resumed run lands a whole board, so anything that spoke here
      // would play a game's worth of sound at once. Whatever a previous turn
      // had queued for a beat that will now never come is dropped with it.
      this.pendingIndustry = undefined;
      // **A free command is not a snap**, and it lands here too — a belt laid
      // or a policy flipped resolves no turn, and it flattens a staged beat
      // that was one frame from its release. The beat's sounds must not go
      // with it (the mint it carried is on the board); a genuine load, replay
      // or undo clears them silently, which is the machine-gun protection the
      // whole channel rests on.
      if (delta.kind === "free") {
        this.announceFactoryBeat();
      } else {
        this.pendingMined = 0;
        // A capture whose landing this snap threw away must not mark the next
        // quiet arrival on that square as a take, turns later.
        this.capturedAt.clear();
      }
      this.industry?.sync(facts);
    }
    this.applyHighlights();
    this.invalidate();
  }

  /**
   * Play the factory as its own beat behind the move, or with it.
   *
   * **It never releases a beat already staged.** Turning it off mid-turn hands
   * the *next* turn over concurrently; the one on screen finishes the way it
   * started, because a factory sync fired from a menu click would land ingots
   * on a board mid-move with nothing having asked for it.
   */
  /**
   * Multiply every playback tempo — the watch transport's speed control.
   *
   * A scale rather than a tempo of its own: the engine's `facts.tempo` already
   * decides how fast somebody else's turn plays by how crowded the run is, and
   * replacing that would make a busy board play slower than an empty one at
   * the same setting.
   *
   * Takes effect on the **next** synced turn, never mid-animation. A tempo
   * changed under a running beat is a piece that arrives somewhere other than
   * where its motion was aimed.
   */
  setPaceScale(scale: number): void {
    this.paceScale = Math.max(0.1, scale);
  }

  /**
   * Cap how often the board draws. `0` is off, which is the default.
   *
   * **Frames, not speed.** Everything still runs at the tempo it was going to;
   * what changes is how many pictures of it get drawn. So it is safe to set
   * mid-animation in a way `setPaceScale` is not — a piece arrives where its
   * motion was aimed either way, it is simply seen fewer times on the way.
   *
   * Scenario games are what it is offered on and the mechanism is general: the
   * case is a machine-vs-machine board, where the renderer and the planner want
   * the same cores and only one of them is doing the thing being watched.
   */
  setMaxFps(fps: number): void {
    this.frameInterval = fps > 0 ? 1 / fps : 0;
    // Both cleared, or the first frame after a change is paced against a gap
    // measured under the old one — and a cap turned off and back on again
    // would bank a burst from however long it was off.
    this.banked = 0;
    this.rawTime = 0;
  }

  setStaging(on: boolean): void {
    this.staging = on;
  }

  /**
   * Whether a look the *renderer* starts may move the view.
   *
   * Every other borrowed look goes through `useCameraLooks`, which reads the
   * two camera toggles; `unmask` cannot, because only the renderer knows the
   * piece has a view to look at. So the answer is pushed here instead — it
   * panned with "Move the camera for me" off, which is the one promise that
   * setting makes. On unless told, so a caller that says nothing keeps the
   * behaviour that shipped.
   */
  setFollowMoves(on: boolean): void {
    this.followMoves = on;
  }

  setHighlights(highlights: BoardHighlights): void {
    this.highlights = highlights;
    this.applyHighlights();
    this.invalidate();
  }

  /**
   * Fade the army back, for a gesture that is about the floor.
   *
   * The belt tools ask about squares the pieces are standing on — a lane may
   * run under one now — so the thing you are aiming at is routinely behind the
   * thing you are not. Held here rather than passed with the highlights: it is
   * a property of the *tool*, which changes about as often as the player
   * changes their mind and never on a state update.
   */
  setPiecesFaded(faded: boolean): void {
    this.faded = faded;
    this.pieces?.setFaded(faded);
    this.invalidate();
  }

  rotate(steps = 1): void {
    this.cameraTaken = true;
    this.rig.rotate(steps);
    this.invalidate();
  }

  /** Pointer-drag turn. Pixels in, radians out — horizontal only; pitch is a preset. */
  turnCamera(dxPixels: number): void {
    this.cameraTaken = true;
    this.rig.turn(dxPixels * DRAG_YAW_PER_PIXEL);
    this.invalidate();
  }

  /** Look across the board or down onto it. See `CameraRig.setTilt`. */
  setTilt(tilt: Tilt): void {
    this.rig.setTilt(tilt);
    this.invalidate();
  }

  /**
   * Point the held pan keys — a direction, not a step. `CameraRig.drive`.
   *
   * **Nothing to convert**: this is a direction and the rig owns the speed, so
   * unlike `panCamera` — which starts from pixels and has to ask the viewport
   * — there is no scale to apply here.
   */
  driveCamera(right: number, forward: number): void {
    // A release is `(0, 0)`, and letting go of a key nobody pressed takes nothing.
    if (right !== 0 || forward !== 0) this.cameraTaken = true;
    this.rig.drive(right, forward);
    this.invalidate();
  }

  /**
   * Slide the view across the board.
   *
   * Pixels become world units here rather than in the rig, because the scale
   * is the viewport's: a camera showing `viewHeight` units over `clientHeight`
   * pixels moves one unit per `clientHeight / viewHeight` pixels, whatever the
   * zoom or the window size. Getting that from a constant would drift the
   * moment either changed.
   *
   * **One to one**, so the ground under the pointer stays under the pointer.
   * A gain above one was tried and it is what a map does *not* do: the board
   * outruns the hand and the square you grabbed is not the square you are
   * holding. Going a long way is the fling's job, and the wheel's.
   *
   * Exact under the orthographic camera and exact **at the focus plane** under
   * the perspective one, which is what `CameraRig.distance` is arranged to
   * buy. A drag that starts on the near rank therefore runs a little slower
   * than the ground under it — the same class of thing as dragging a map at a
   * tilt, and the reason the lens is a long one.
   */
  panCamera(dxPixels: number, dyPixels: number): void {
    const height = this.canvas.clientHeight || 1;
    const perPixel = this.rig.viewHeight / height;
    // Divided by sin(pitch): the board is seen at an angle, so a pixel of
    // vertical drag covers more ground than a pixel of horizontal drag.
    const pitchScale = Math.max(0.25, Math.sin(this.rig.pose.pitch));
    this.cameraTaken = true;
    this.rig.pan(dxPixels * perPixel, (dyPixels * perPixel) / pitchScale);
    this.invalidate();
  }

  /**
   * Zoom about a point on the board, in client coordinates.
   *
   * `notches` is signed the way a wheel is: positive scrolls away and pulls
   * back. The exponential is what makes the wheel feel even — a linear step
   * on a view height is a huge change when zoomed in and an imperceptible one
   * when zoomed out, so the same flick does a different amount depending on
   * where you started.
   */
  zoomAt(clientX: number, clientY: number, notches: number): void {
    this.zoomBy(clientX, clientY, Math.exp(notches * ZOOM_PER_NOTCH));
  }

  /**
   * Zoom by a factor about a point, in client coordinates.
   *
   * The wheel's door and the pinch's, so the pin is worked out once: a wheel
   * notch is a factor and so is a pinch, and two call sites raycasting their
   * own ground point is two places for the pin to drift from the gesture.
   */
  zoomBy(clientX: number, clientY: number, factor: number): void {
    this.cameraTaken = true;
    this.rig.zoom(factor, this.groundPoint(clientX, clientY));
    this.invalidate();
  }

  /** Pull back until the whole board is on screen. */
  fitView(): void {
    this.rig.fit();
    this.invalidate();
  }

  /**
   * Where a client point meets the board plane, or undefined if it misses.
   *
   * A mathematical plane rather than the pick mesh: this has to answer for
   * ground that may be well off the board, which is exactly where a zoom-out
   * leaves the cursor.
   */
  private groundPoint(clientX: number, clientY: number): { x: number; z: number } | undefined {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(this.pointer, this.rig.camera);
    const hit = this.raycaster.ray.intersectPlane(GROUND_PLANE, this.deckHit);
    return hit ? { x: hit.x, z: hit.z } : undefined;
  }

  /**
   * Widen the board directly. For the smoke, which needs a board bigger
   * than the viewport without playing a game to get one — and which should not
   * have to know how the board's extent is stored.
   */
  growForTests(minFile: number, maxFile: number, minRank: number, maxRank: number): void {
    this.setPanRegionFor({ minFile, maxFile, minRank, maxRank });
  }

  /** Every square that exists, as a world rectangle. The rig insets it by the view. */
  private setPanRegionFor(bounds: Bounds): void {
    const west = fileToX(bounds.minFile) - 0.5;
    const east = fileToX(bounds.maxFile) + 0.5;
    // rankToZ inverts, so the *max* rank is the smallest z.
    const far = rankToZ(bounds.maxRank) - 0.5;
    const near = rankToZ(bounds.minRank) + 0.5;
    this.rig.setBoardExtent(west, east, far, near);
  }

  /** Which way the board is facing on screen. See `CameraRig.viewYaw`. */
  get viewYaw(): number {
    return this.rig.viewYaw;
  }

  /** How much board is on screen vertically, in squares. For the smokes. */
  get viewHeight(): number {
    return this.rig.viewHeight;
  }

  /** The pointer let go: the camera stops tracking and eases from here. */
  endCameraDrag(): void {
    this.rig.release();
    this.invalidate();
  }

  /** The gesture was taken away instead: settle it with no throw. `CameraRig.cancel`. */
  cancelCameraDrag(): void {
    this.rig.cancel();
    this.invalidate();
  }

  /**
   * Point the camera at the middle of a group of squares.
   *
   * Single player uses this to lead the action: the board is bigger than the
   * viewport once it has grown a few times, so an opponent's move or a camp
   * landing can happen entirely off screen. Moving the view *before* the thing
   * happens is the difference between watching a game and being told about
   * one afterwards.
   */
  /**
   * Play the reaction for every costume that came off this turn.
   *
   * **The camera is borrowed only if there is something to see**, which is why
   * `PieceViews.bounce` answers a boolean: a view exists for a piece that is
   * on the board and drawn, and a reaction nobody can look at is not worth
   * moving the board for. `flyIn` reports the same way for the same reason.
   *
   * **A `"move"` look and not an `"event"`**, deliberately. The classic-board
   * gate and the "is it already on screen" test are exactly right here: an
   * unmasking happens on somebody's ordinary move, several times a run, and
   * recentring the board for one the player is already watching is the
   * every-turn camera-flying this project measured and switched off. A front
   * landing is the rare, large case that earns an event; this is not it.
   *
   * `lookAtSquares` is called directly rather than through the driver's `look`
   * because the trigger is a fact the *renderer* has — it is the only thing
   * that knows whether the piece has a view at all.
   */
  private unmask(squares: readonly number[] | undefined): void {
    if (!squares?.length) return;
    const seen: number[] = [];
    for (const square of squares) if (this.pieces?.bounce(square)) seen.push(square);
    if (seen.length === 0) return;
    // Only when it is off screen, the same courtesy an ordinary move gets —
    // `onScreen` is the one test both go through, so a reaction cannot be the
    // one thing that yanks the board about.
    // And only if a move may take the view at all: `setFollowMoves`.
    if (this.followMoves && !this.onScreen(seen)) this.lookAtSquares(seen);
    this.invalidate();
  }

  /**
   * `remember` makes the look the *player's*: it moves the anchor, so nothing
   * hands the board back afterwards. False for every borrowed look — the
   * driver's, the director's — and true for a jump the player asked for, such
   * as following the edge marker to the last move.
   */
  lookAtSquares(squares: readonly number[], fit = false, remember = false): void {
    if (squares.length === 0) return;
    // **New ground: pull back rather than slide across.** Panning to a region
    // that has just appeared frames the region and hides how it joins on to
    // the board you were playing — which is the one thing worth seeing at that
    // moment. Borrowed like the pan, so `returnView` still works, and a no-op
    // when everything already fits.
    if (fit) {
      this.rig.frameBoardBorrowed();
      this.invalidate();
      return;
    }
    let x = 0;
    let z = 0;
    for (const square of squares) {
      const world = squareToWorld(square, 0);
      x += world.x;
      z += world.z;
    }
    // Borrowed unless the caller says otherwise: a borrowed look leaves the
    // player's anchor alone, so `returnView` hands the board back exactly
    // where they left it.
    this.rig.focusOn(x / squares.length, z / squares.length, remember);
    this.invalidate();
  }

  /**
   * Are all these squares comfortably on screen already?
   *
   * What a per-move look asks before it takes the view. The board is drawn in
   * a fixed row between the header and the shelf, so "the viewport" here is
   * the canvas — which is what the rig measures against anyway.
   *
   * Every square, not the middle of them: a move is a from and a to, and one
   * of them being off screen is precisely the case where the player needs the
   * camera. Empty is `true` — there is nothing to fail to see.
   */
  /**
   * Where the board's middle currently lands, in viewport pixels.
   *
   * For the interface to get out of its way: a panel that knows which side the
   * board is on can take the other one. Projected from the board's *bounds*
   * rather than from a square, because a grown board is ragged and its middle
   * is routinely a hole — `pickPoint` would answer null for exactly the boards
   * this matters most on.
   */
  boardScreenCentre(): { x: number; y: number } | null {
    const bounds = this.facts?.bounds;
    if (!bounds) return null;
    const middle = squareToWorld(
      square(
        Math.round((bounds.minFile + bounds.maxFile) / 2),
        Math.round((bounds.minRank + bounds.maxRank) / 2)
      )
    );
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const point = new Vector3(middle.x, 0, middle.z).project(this.rig.camera);
    return {
      x: rect.left + ((point.x + 1) / 2) * rect.width,
      y: rect.top + ((1 - point.y) / 2) * rect.height
    };
  }

  /**
   * Tell the camera how much of the canvas the interface is covering.
   *
   * See `CameraRig.setViewInset`: the board is drawn behind the whole page,
   * and this is what keeps the part of it you can *reach* centred.
   */
  setViewInset(inset: { left: number; right: number; top: number; bottom: number }): void {
    const moved = this.rig.setViewInset(inset);
    // **Re-fit, or the board is centred correctly and still too big.** The
    // inset says where the free area *is*; it does not change how much board
    // the zoom is showing, so an opening framed against the whole canvas keeps
    // its edges under the panel — measured as f6 being unreachable at 1280
    // wide, with every candidate point for it covered.
    //
    // Only while nothing has been played, which is the same condition the
    // opening framing uses: yanking the camera because a panel changed sides
    // mid-game would be the interface moving the board out from under the
    // player, which is the thing the whole layout is trying not to do.
    //
    // **And only when the free area really moved and the camera is not yet the
    // player's.** The registry republishes for the inspector too — `edge: null`,
    // so the insets it folds to are the same four numbers — and re-fitting on
    // that made every selection before the first move a camera reset: zoom in,
    // click the king, and the view pulled back out and re-centred.
    if (moved && !this.cameraTaken && (this.status?.ply ?? 0) === 0) this.fitView();
    this.invalidate();
  }

  onScreen(squares: readonly number[], inset = COMFORTABLE): boolean {
    for (const square of squares) {
      const world = squareToWorld(square, 0, this.lookHit);
      if (!this.rig.covers(world.x, world.z, inset)) return false;
    }
    return true;
  }

  /** Put the view back where the player last left it. */
  returnView(): void {
    this.rig.returnToAnchor();
    this.invalidate();
  }

  /**
   * True when nothing is still moving: no piece mid-tween, no camera ease, no
   * tile rising. What "the board has finished doing what it was doing" means.
   */
  /**
   * How many squares a highlight is lighting — what the *board* is offering,
   * as opposed to what the state says. The seat test needs the first: a client
   * that cannot move a piece must not light its moves either.
   */
  highlightCount(kind: HighlightKind): number {
    return this.tiles.highlightCount(kind);
  }

  get boardSettled(): boolean {
    return !this.busy;
  }

  /**
   * How much animation time this board is getting per second of wall clock.
   *
   * 1 while every frame comes in under the step ceiling, which is the ordinary
   * case and the only one on a machine keeping up. Below it, the driver is
   * dealing commands faster than the board can draw what they mean, and the
   * driver is the only thing that can do anything about it — see `clockHealth`.
   *
   * **Deliberately not `boardSettled`.** That answers whether anything is
   * animating *now*; this is a rate, and a rate cannot deadlock anything.
   */
  get clockHealth(): number {
    return clockHealth(this.healthAdvanced, this.healthElapsed);
  }

  /** Put the middle of the board back in the middle of the screen. */
  centreCamera(): void {
    this.rig.centre();
    this.invalidate();
  }

  /** Back to the default angle and zoom, keeping the current facing. */
  resetView(): void {
    this.rig.resetView();
    this.invalidate();
  }

  /**
   * Orthographic or perspective. See `CameraRig`'s note for why the first is
   * the design and the second is a toggle.
   *
   * `invalidate` is not optional here even though nothing moved: the render
   * loop is on demand, so a switch made on a settled board would sit in the
   * rig with the last frame still on screen until something else happened to
   * ask for a redraw.
   */
  setProjection(mode: Projection): void {
    this.rig.setProjection(mode);
    this.invalidate();
  }

  get quarterTurns(): number {
    return this.rig.quarterTurns;
  }

  /**
   * Which direction arrow is under a client point, or -1 when the picker is
   * closed or the point misses it.
   *
   * Must be consulted BEFORE `pick`: the arrows hang out over neighbouring
   * tiles, so a click on one also resolves to a square underneath, and taking
   * the square would silently do something else entirely.
   */
  pickDirection(clientX: number, clientY: number): number {
    const rect = this.canvas.getBoundingClientRect();
    return this.picker.pick(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
      this.rig.camera
    );
  }

  /**
   * Square under a client-space point, or -1.
   *
   * `throughPieces` is what the belt tools use. A piece is a pick target
   * because it is a thing you click, and at a 3/4 pitch a tall one covers most
   * of the square *behind* it — so dragging a lane across the board caught on
   * every piece it passed, resolving to the piece's square instead of the
   * ground the pointer was actually over, and the run kinked round the army it
   * was supposed to run under. When the gesture is about the floor, the pieces
   * are not in the way of it.
   */
  /**
   * Is a square that is not board worth returning right now?
   *
   * Two things make it so, and they are the two ways a square can be off the
   * board while still being something a player must be able to click: Bridges
   * tiles waiting to go down, and a Spatial Manifold's edge marks (§26 C10),
   * which are beyond the frontier because a *direction* is picked as a square.
   * One flag rather than two, because `pick` has one question to answer.
   *
   * Held on the renderer rather than passed to `pick`, because the callers of
   * `pick` are pointer handlers that know about screens and not about runs —
   * and every one of them would have had to be taught the same fact.
   */
  private offBoard = false;

  setOffBoard(on: boolean): void {
    this.offBoard = on;
  }

  pick(clientX: number, clientY: number, throughPieces = false): number {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(this.pointer, this.rig.camera);

    // Nearest hit wins, over pieces AND the ground plane together. Taking the
    // first intersecting piece in iteration order is wrong: at a 3/4 pitch a
    // tall piece occludes most of the square behind it, so several pieces can
    // sit under one pixel and only the closest is the one being looked at.
    let best = Infinity;
    let square = -1;

    for (const target of throughPieces ? [] : this.pieces?.pickTargets() ?? []) {
      const [hit] = this.raycaster.intersectObject(target.object, true);
      if (hit && hit.distance < best) {
        best = hit.distance;
        square = target.square;
      }
    }

    // The belt deck, as a plane.
    //
    // Belts are not pick targets — they are instanced and there is one mesh
    // for the whole network — so the ray used to pass straight through a lane
    // and land on the flat ground *beyond* it. At a 3/4 pitch that error
    // displaces the hit along the view direction, which on the board is both a
    // file and a rank: clicking a belt resolved to the square diagonally
    // behind it, and the move you were trying to make silently did not happen.
    // Reported as "cannot move onto belts along diagonals", and the diagonal
    // is the *symptom* rather than the cause.
    //
    // Intersecting a raised plane costs nothing and answers exactly the right
    // question: what is under the pointer at the height a lane is drawn at.
    // Only when that square really has a belt, or a click over empty board
    // would be pulled a square toward the camera.
    const deck = this.raycaster.ray.intersectPlane(BELT_PLANE, this.deckHit);
    if (deck && this.facts) {
      const above = worldToSquare(deck.x, deck.z);
      const distance = this.raycaster.ray.origin.distanceTo(deck);
      const at = this.facts.squares.get(above as Square);
      if (
        above >= 0 &&
        distance < best &&
        at?.cell === "board" &&
        // **A belt, not a drawn part.** A ghost is not a thing on the board:
        // it is a claim about where track is going, it can be edited only with
        // the build tools that drew it, and it must not be what a click in the
        // pieces view lands on. Keeping it out of the deck test is also what
        // lets it render at its true depth — an overlay that can be clicked
        // has to be visible, and one that cannot be does not.
        builtBelt(at) !== undefined
      ) {
        best = distance;
        square = above;
      }
    }

    // The board plane, as a plane rather than as the mesh it used to be.
    //
    // That mesh was forty units square, which was every square there was when
    // it was written and is now a hole in the middle of a board that grows —
    // click empty ground more than twenty units out and the ray met nothing,
    // so the click resolved to -1 and silently did nothing. Zooming out makes
    // that ground easy to reach, which is how it would have been found.
    const ground = this.raycaster.ray.intersectPlane(GROUND_PLANE, this.groundHit);
    if (ground && this.raycaster.ray.origin.distanceTo(ground) < best) {
      square = worldToSquare(ground.x, ground.z);
    }

    // Off the board is not a square. The check lives here rather than in
    // `worldToSquare` because which squares exist is a property of the
    // position — and it grows.
    //
    // A building is the exception, and it has to be: its square is no longer a
    // cell — that is the whole encoding, and the facts say `structure-floor`
    // — so without this a store would be a hole you cannot click, and the one
    // thing a player most wants to do with a warehouse is look inside it.
    if (
      square >= 0 &&
      this.facts &&
      // **Free Space is the second exception, and it is the opposite one.** A
      // building is off the board because its square left `cells`; a tile is
      // off the board because it has not arrived yet. Both are things a player
      // must be able to click, and neither is a cell — so while there are
      // tiles to place, a square that is not board is exactly the target. A
      // manifold's edge mark (§26 C10) is the same exception for the same
      // reason, which is why `offBoard` is one flag and not two.
      !this.offBoard &&
      (this.facts.squares.get(square as Square)?.cell ?? "none") === "none"
    ) {
      return -1;
    }

    return square;
  }

  /**
   * A client point that actually resolves back to `square`, or null if the
   * square is entirely hidden behind other pieces.
   *
   * Occlusion is real and measured: at this pitch a king hides most of the
   * square behind it, so the centre of a tile is not reliably clickable. Probe
   * outward from the centre and return the first point that round-trips
   * through `pick`. Used by the smoke test, and by any UI that needs to anchor
   * something to a square.
   */
  pickPoint(square: number): { x: number; y: number } | null {
    // Centre first, then toward the viewer, then the corners — in rough order
    // of how likely each is to be visible.
    const offsets: [number, number][] = [
      [0, 0],
      [0, 0.34],
      [0.3, 0.3],
      [-0.3, 0.3],
      [0.34, 0],
      [-0.34, 0],
      [0, -0.34],
      [0.3, -0.3],
      [-0.3, -0.3]
    ];

    const centre = squareToWorld(square);
    const rect = this.canvas.getBoundingClientRect();

    for (const [dx, dz] of offsets) {
      const point = new Vector3(centre.x + dx, 0, centre.z + dz).project(this.rig.camera);
      // Round BEFORE verifying. Pointer events arrive at integer coordinates,
      // so a fractional point that happens to clear an occluding piece by less
      // than a pixel validates here and then resolves to the *other* piece when
      // actually clicked — a real bug that presented as "the click silently
      // does nothing", and only on squares behind a tall piece.
      const client = {
        x: Math.round(rect.left + ((point.x + 1) / 2) * rect.width),
        y: Math.round(rect.top + ((1 - point.y) / 2) * rect.height)
      };
      // **And nothing must be sitting on top of it.** This answers "where do I
      // click to hit this square", and a raycast alone stopped being enough
      // the moment the canvas went behind the whole page: a point can resolve
      // to the right square and still land on the panel or the header, where
      // the click reaches those instead and the board never hears it. The view
      // inset keeps the *board* out from under the interface; this is what
      // keeps the promise honest for anything that still overlaps.
      if (
        typeof document !== "undefined" &&
        document.elementFromPoint(client.x, client.y) !== this.canvas
      ) {
        continue;
      }
      if (this.pick(client.x, client.y) === square) return client;
    }

    return null;
  }

  /**
   * The body in the player's hand, and what is carrying it. See `carryView.ts`.
   */
  private carry?: {
    spec: CarrySpec;
    object: Object3D;
    motion: CarryMotion;
    end(dropped: boolean): void;
  };
  private carryPoint = new Vector3();

  /**
   * Pick a body up: a piece off its square, or a new one off the hotbar.
   *
   * Nothing about the game moves — this is a drawing of a click that has not
   * happened yet, and the drop is `pickSquare` on the square `moveCarry` names.
   */
  beginCarry(asked: ApiCarrySpec, clientX: number, clientY: number): void {
    this.endCarry(false);
    if (!this.pieces || !this.industry || !this.facts) return;
    // The contract's names, keyed for the meshes — the one place a carry crosses.
    const spec: CarrySpec = {
      targets: asked.targets,
      ghost:
        asked.ghost.kind === "held"
          ? asked.ghost
          : asked.ghost.kind === "piece"
            ? {
                kind: "piece",
                piece: packedPiece({ type: asked.ghost.type, side: asked.ghost.side }),
                ...(asked.ghost.facing ? { facing: DIR_INDEX[asked.ghost.facing] } : {})
              }
            : { kind: "structure", structure: STRUCTURE_KIND[asked.ghost.structure], owner: asked.ghost.owner }
    };
    const { ghost } = spec;
    let object: Object3D;
    let end: (dropped: boolean) => void;
    if (ghost.kind === "held") {
      const held = this.pieces.hold(ghost.square);
      if (!held) return;
      object = held.object;
      end = held.release;
    } else if (ghost.kind === "piece") {
      const pieces = this.pieces;
      const side = pieceColor(ghost.piece);
      const facing = this.facts.armyFacing.get(side as ApiSide);
      const army = facing === undefined ? forwardFacing(side) : DIR_INDEX[facing];
      const body = pieces.createGhost(ghost.piece, ghost.facing ?? army, army);
      object = body;
      end = () => pieces.discardGhost(body);
    } else {
      const body = this.industry.structureGhost(ghost.structure, ghost.owner);
      object = body.object;
      end = body.dispose;
    }
    // A new body starts in the hand; a held one starts where it stands and is
    // taken up to the pointer by the motion's first jump.
    if (ghost.kind !== "held" && this.carryFree(clientX, clientY)) {
      object.position.copy(this.carryPoint);
    }
    this.carry = { spec, object, motion: new CarryMotion(object.position), end };
    this.moveCarry(clientX, clientY);
  }

  /**
   * The pointer moved. Returns the square a release here would drop on, or
   * `CARRY_FREE` — and the body is drawn on that square, which is the promise.
   */
  moveCarry(clientX: number, clientY: number, snap = true): number {
    const carry = this.carry;
    if (!carry) return CARRY_FREE;
    const square = snap ? this.carrySquare(clientX, clientY) : CARRY_FREE;
    if (square >= 0) {
      const seat = (this.pieces?.seatY(square) ?? 0) + CARRY_SNAP_LIFT;
      carry.motion.aim(squareToWorld(square, seat, this.carryPoint), square);
    } else if (this.carryFree(clientX, clientY)) {
      carry.motion.aim(this.carryPoint, CARRY_FREE);
    }
    this.invalidate();
    return square;
  }

  /** Put it down. `dropped` says a square took it, so a held piece stays. */
  endCarry(dropped: boolean): void {
    const carry = this.carry;
    if (!carry) return;
    this.carry = undefined;
    carry.end(dropped);
    this.invalidate();
  }

  /**
   * The square a drop here lands on, or `CARRY_FREE`.
   *
   * **What a click here would resolve to, then the floor under it.** The
   * first is what makes dropping on an enemy's body a capture, as clicking it
   * is; the second is for a target behind one of your own pieces, which a
   * click reaches by aiming round it and a body in the hand cannot.
   */
  carrySquare(clientX: number, clientY: number): number {
    const targets = this.carry?.spec.targets;
    if (!targets) return CARRY_FREE;
    const direct = this.pick(clientX, clientY);
    if (targets.includes(direct)) return direct;
    const floor = this.pick(clientX, clientY, true);
    return targets.includes(floor) ? floor : CARRY_FREE;
  }

  /** Where a free body floats under this point, into `carryPoint`. */
  private carryFree(clientX: number, clientY: number): boolean {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(this.pointer, this.rig.camera);
    const hit = this.raycaster.ray.intersectPlane(GRIP_PLANE, this.carryPoint);
    if (hit) hit.y = CARRY_LIFT;
    return hit !== null;
  }

  /**
   * Hand the camera to a side, easing from wherever it is now.
   *
   * Each colour keeps its own viewpoint, and whatever the camera was doing
   * when it changes hands is saved as the outgoing side's. Two people sharing
   * a screen each get the angle they set, and the board turns to meet whoever
   * is on the clock rather than making them re-orient every turn.
   *
   * Black's default is White's turned half round, so the first handover
   * already faces the right way without anyone touching the controls.
   */
  focusSide(side: Side): void {
    if (side === this.viewpoint) return;
    this.sides = Math.max(this.sides, side + 1);

    const outgoing = this.rig.pose;
    this.poses[this.viewpoint] = outgoing;
    this.claimed[this.viewpoint] = true;

    if (!this.claimed[side]) {
      // Each new army gets an even share of the circle away from the outgoing
      // view, so a third player's default is its own angle rather than one it
      // shares with someone else. With two sides this is the half-turn it has
      // always been.
      const share = (2 * Math.PI) / Math.max(2, this.sides);
      this.poses[side] = {
        yaw: outgoing.yaw + share * Math.max(1, side - this.viewpoint),
        pitch: outgoing.pitch,
        focusX: outgoing.focusX,
        focusZ: outgoing.focusZ
      };
      this.claimed[side] = true;
    }

    this.viewpoint = side;
    this.rig.goTo(this.poses[side]);
    this.invalidate();
  }

  /**
   * Is there anything on this square worth clicking — a piece, a part, or a
   * heap? Used only to choose a cursor, so it is deliberately coarse: it
   * answers "is this square inert", not "what would happen".
   */
  hasSomethingAt(square: number): boolean {
    const at = this.facts?.squares.get(square as Square);
    if (!at) return false;
    return at.piece !== undefined || builtBelt(at) !== undefined || at.structure !== undefined || at.heap !== undefined;
  }

  /** Flash a piece to show it just did something. */
  pulse(square: number): void {
    this.pieces?.pulse(square);
    this.invalidate();
  }

  /**
   * Screen position of a square's bottom-right corner, for anchoring an
   * overlay that should sit *beside* the piece rather than on top of it.
   *
   * "Bottom right" is resolved in screen space, not board space: the camera
   * yaws in ninety-degree steps and drags freely, so the corner that reads as
   * bottom-right changes as it turns. Projecting all four and taking the
   * largest x+y follows the camera for free.
   */
  projectSquareCorner(square: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const centre = squareToWorld(square);
    let best = { x: -Infinity, y: -Infinity };

    for (const [dx, dz] of CORNERS) {
      const point = new Vector3(centre.x + dx, 0, centre.z + dz).project(this.rig.camera);
      const client = {
        x: rect.left + ((point.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - point.y) / 2) * rect.height
      };
      if (client.x + client.y > best.x + best.y) best = client;
    }

    return best;
  }

  /**
   * True when the camera is not mid-move.
   *
   * The camera changes hands on every turn now, so anything that projects a
   * square to a screen point and then clicks it has to wait for the board to
   * stop — otherwise the point is stale by the time the click lands and
   * resolves to a different square. The e2e smokes wait on this.
   */
  /**
   * Swap the piece models. Developer-only; see `PIECE_PACKS`.
   *
   * The board is redrawn from the current state rather than rebuilt: a pack
   * changes what a rank looks like and nothing about where anything is.
   */
  async setPiecePack(pack: PiecePack, side: number | "default" = "default"): Promise<void> {
    // **Wait for the views, not just for the pack.** `this.pieces` is built in
    // the `ready` promise, so a pack that finishes loading first found no views
    // to dress and returned silently. Locally `ready` always won — it is one
    // cached bundle against six small files — and over a real network it did
    // not, which is exactly why `?pack=` worked in dev and booted the base set
    // in production. Both loads still run at once; only the write waits.
    const [loaded] = await Promise.all([loadPiecePack(pack), this.ready]);
    if (this.disposed || !this.pieces) return;
    this.pieces.setGeometries(
      loaded.geometries,
      loaded.materials,
      PACK_YAW[pack],
      PIECE_YAW[pack],
      side,
      loaded.masks,
      PACK_PAINT[pack],
      loaded.variants
    );
    // **The pack's own conveyor, or back to the kit's.** Passed even when the
    // pack has none, because a swap *away* from a pack that had one has to put
    // Kenney's back — the board after a round trip must be the board that never
    // left. `industry` may not exist yet on the very first sync; `ready` has
    // resolved by here, so in practice it does.
    this.industry?.setBeltGeometries(loaded.belts);
    // **Re-arm the arrival shot.** `setGeometries` clears every view, so a
    // king that was flying in has just been thrown away mid-descent — and this
    // runs on every mount, a few hundred milliseconds after a run opens, which
    // is precisely when the shot is playing. Relaunched rather than resumed:
    // the swap happens early enough that starting again from the top is
    // invisible, and resuming would mean carrying flight state across the one
    // operation whose whole job is to throw view state away. `flyKingIn` is
    // still gated on the run being at ply zero, so a dev-menu pack switch
    // twenty moves in re-arms nothing.
    this.flownIn = false;
    if (this.facts) this.syncPieces(this.facts, null);
    this.invalidate();
  }

  get cameraSettled(): boolean {
    return this.rig.settled;
  }

  /** Rendered facing of the pawn on a square, in radians, or null. */
  pawnFacingYaw(square: number): number | null {
    return this.pieces?.facingYaw(square) ?? null;
  }

  /**
   * A client point that resolves to the open picker's arrow for `direction`,
   * or null when the picker is closed or that arrow cannot be hit.
   *
   * The same round-trip discipline as `pickPoint`, and for the same reason:
   * the point is rounded to integers *before* being verified through
   * `pickDirection`, because that is the precision a real pointer event has.
   */
  directionPoint(direction: number): { x: number; y: number } | null {
    const world = this.picker.arrowCentre(direction);
    if (!world) return null;

    const rect = this.canvas.getBoundingClientRect();
    const point = world.project(this.rig.camera);
    const client = {
      x: Math.round(rect.left + ((point.x + 1) / 2) * rect.width),
      y: Math.round(rect.top + ((1 - point.y) / 2) * rect.height)
    };
    return this.pickDirection(client.x, client.y) === direction ? client : null;
  }

  /** Pieces currently in the scene. Zero after load means a broken asset pipeline. */
  get pieceCount(): number {
    return this.pieces?.pickTargets().length ?? 0;
  }

  /**
   * Centre of a square in client coordinates — the inverse of `pick`.
   * Exists so the Playwright smoke test can click real squares rather than
   * reaching past the UI into the store.
   */
  projectSquare(sq: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const point = squareToWorld(sq).project(this.rig.camera);
    return {
      x: rect.left + ((point.x + 1) / 2) * rect.width,
      y: rect.top + ((1 - point.y) / 2) * rect.height
    };
  }

  resize(): void {
    const width = this.canvas.clientWidth || 1;
    const height = this.canvas.clientHeight || 1;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);
    this.rig.setPixelSize(width, height);
    this.rig.setAspect(width / height);
    // The rims are measured in pixels, so they are the one thing on the board
    // that has to be told the viewport rather than the world.
    this.outlines.setSize(width, height);

    // Paint *now*, not on the next frame.
    //
    // `setSize` reallocates the drawing buffer and the new one starts empty,
    // so anything that changes the canvas's size — a panel opening, a bar
    // growing under it, the window being dragged — showed one frame of black
    // while the render loop caught up. A resize observer runs before paint, so
    // drawing here means the browser never composites the empty buffer.
    if (!this.disposed) this.renderer.render(this.scene, this.rig.camera);
    this.needsRender = false;
  }

  dispose(): void {
    this.endCarry(false);
    this.disposed = true;
    cancelAnimationFrame(this.frame);
    this.tiles.dispose();
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.picker.dispose();
    this.pieces?.dispose();
    this.industry?.dispose();
    this.outlines.dispose();
    this.renderer.dispose();
  }

  /**
   * The army, and what just moved.
   *
   * `delta` is the change being animated, or null for a board that arrived
   * all at once. Its legs are the engine's: the mover, a castle's rook, the
   * piece a blow drove back (§27), the piece a Hydraulic Clasp dragged (§26
   * C11) — each moved, none of them a second event.
   */
  private syncPieces(facts: BoardFacts, delta: TurnDelta | null): void {
    if (!this.pieces) return;

    const legs: readonly Leg[] = delta?.kind === "move" || delta?.kind === "tick" ? delta.legs : [];
    const moved: { from: number; to: number }[] = [];
    if (delta?.kind === "move") {
      const mover = legs.find((leg) => leg.role === "mover");
      if (mover) {
        // **The mover, and only the mover.** The rook goes with its king;
        // announcing it would be two of one event.
        const capture = delta.captures.length > 0;
        // Remembered for the landing: by the time the model stops, the victim
        // is gone and nothing on the board says it was ever there.
        if (capture) this.capturedAt.add(mover.to);
        this.emitSound({
          kind: "move-started",
          from: mover.from,
          to: mover.to,
          distance: Math.max(
            Math.abs(fileOf(mover.to) - fileOf(mover.from)),
            Math.abs(rankOf(mover.to) - rankOf(mover.from))
          ),
          capture
        });
      }
    }
    moved.push(...legs);

    this.pieces.sync(facts, moved, 0);
    this.flyKingIn(facts);
  }

  /**
   * The opening shot: a run's king arrives under its own power.
   *
   * The trigger is a question about the *game* — is this the first frame of a
   * run — so it lives here rather than in `PieceViews`, which knows only about
   * squares and meshes. Runs only: a versus game opens with two armies already
   * deployed, and flying one king in would be a claim about whose game it is.
   *
   * **Latched rather than fired on a state change**, because a board at ply
   * zero syncs many times over — a belt laid, a piece selected, the level
   * landing — and each would relaunch a king that was halfway down. The latch
   * clears the moment the run is played or left, so restarting a level plays
   * it again.
   *
   * `flyIn` answering false is the ordinary case on the very first sync: the
   * GLBs are still in flight, there are no views yet, and the ready handler
   * syncs again the moment they land.
   */
  private flyKingIn(facts: BoardFacts): void {
    if (!facts.run || (this.status?.ply ?? 0) > 0) {
      this.flownIn = false;
      return;
    }
    if (this.flownIn) return;
    const king = facts.run.kingAt;
    if (king === undefined) return;
    this.flownIn = this.pieces?.flyIn(king) ?? false;
  }

  private applyHighlights(): void {
    const {
      selected,
      legalMoves = [],
      spawns = [],
      captures = [],
      reAimTargets = [],
      directionPicker = null
    } = this.highlights;
    this.tiles.setHighlight("extent", this.highlights.extent ?? []);
    this.tiles.setHighlight("selection", selected === undefined ? [] : [selected]);
    // **The selected square is what the arrows point away from.** A legal-move
    // mark is one end of a line, and the other end is whatever is selected —
    // so the arrow on each square lies along the move it stands for. With
    // nothing selected there is no line to draw, and `setHighlight` leaves the
    // art pointing the way it was drawn.
    this.tiles.setHighlight("legalMove", legalMoves, selected);
    // Not aimed: a piece appearing is not a journey, so `line_cross` is
    // symmetric and has no bearing to be right or wrong about.
    this.tiles.setHighlight("spawn", spawns);
    this.industry?.setGhosts(this.highlights.beltPreview ?? []);
    this.tiles.setHighlight("capture", captures);
    this.tiles.setHighlight("shove", this.highlights.shoves ?? []);
    this.tiles.setHighlight("reAimTarget", reAimTargets);
    // The centre "remove" target only where there is something to remove: a
    // part of the player's own. On a pawn being re-aimed there is nothing to
    // take away, and offering it would be a control that does nothing.
    const onBelt =
      directionPicker != null &&
      builtBelt(this.facts?.squares.get(directionPicker as Square)) !== undefined;
    // **Bigger over a part than over a pawn**, because it is doing more.
    //
    // A re-aim asks one question and the arrows are the whole control. A part
    // opens *five* targets on one square — four directions and a remove — with
    // a lane, its cargo and whatever is standing there underneath them, and at
    // the picker's original size the remove disc and the arrow tails were
    // close enough that a slightly off click took up a part you meant to turn.
    this.picker.show(directionPicker, onBelt, onBelt ? PICKER_ON_BELT : 1);

    const facts = this.facts;
    if (!facts) return;

    // **Every royal under attack, not merely the one that binds.** A side
    // holding a spare king is not in check at all — that is the backup rule —
    // but the attack is still the most important thing on the board for the
    // player to see, and it is the thing that decides which king to move. So
    // the fact is `royalThreatened` rather than "in check": with one king the
    // two answer the same square, and with two it is the difference between
    // the board saying "this is being hunted" and saying nothing.
    const threatened: number[] = [];
    const owned: number[][] = [[], []];
    const anyones: number[] = [];
    for (const [sq, at] of facts.squares) {
      if (at.royalThreatened) threatened.push(sq);
      if (at.promotion === undefined) continue;
      // Sides past the two the palette pair covers fall back to the neutral
      // marker rather than being drawn as somebody else's. Nothing creates one
      // today; drawing it in the wrong army's colour would be worse than
      // drawing it in nobody's.
      if (at.promotion === 0 || at.promotion === 1) owned[at.promotion].push(sq);
      else anyones.push(sq);
    }
    this.tiles.setHighlight("check", threatened);

    // The three highlights that are about a *thing* rather than about a
    // square, so the three that get an inverted hull round the thing itself.
    // The rest stay markers on the floor: a legal move is a square, a
    // promotion square is a square, and a march's destination is a region.
    //
    // **Alongside the markers, never instead of them.** A ring says where, a
    // rim says what, and a board that answered "where" one way for an empty
    // square and another for an occupied one would be two languages.
    //
    // One rim per square, so the order is a ranking and this is it: being in
    // check outranks being takeable outranks being picked. Selection loses
    // because it is the one the player already knows about — they just
    // clicked it — and it is the one the floor marker under it is clearest
    // about.
    const outlined = new Map<number, number>();
    // A rim on each, in the same order: a square wears one rim and the rest of
    // this block overwrites by priority, so listing them all here lets a
    // selected or capturable royal still take its own colour.
    for (const royal of threatened) outlined.set(royal, PALETTE.check);
    for (const square of captures) {
      if (!outlined.has(square)) outlined.set(square, PALETTE.capture);
    }
    if (selected !== undefined && !outlined.has(selected)) {
      outlined.set(selected, PALETTE.selection);
    }
    // Kept, because it can be asked for before the models land — the same
    // reason the fade and the belt preview are.
    this.outlined = outlined;
    // Both halves take the whole map: a square holds a piece or a building and
    // never both, so each ignores what is not its own.
    this.pieces?.setOutlines(outlined);
    this.industry?.setOutlines(outlined);

    // Standing ground, not a response to a selection: a promotion square is
    // lit from the moment its region is revealed until it is spent, because
    // its whole job is to be somewhere a player plans a march toward.
    //
    // **Split by whose it is, because on some boards that is the rule.** An
    // owned marker promotes for one side alone (`promotionOwner`), and a board
    // that drew every marker the same gold would be stating the opposite of
    // what it does — the Factory Map's two lines sit behind opposite armies,
    // so "there is promotion ground behind me" reads as an opening rather than
    // as the enemy's finish line. Unowned stays gold: a run's revealed squares
    // genuinely are anybody's.
    this.tiles.setHighlight("promotionSquare", anyones);
    this.tiles.setHighlight("promotionWhite", owned[0]);
    this.tiles.setHighlight("promotionBlack", owned[1]);
  }

  private invalidate(): void {
    this.needsRender = true;
  }

  /**
   * Run `listener` after each rendered frame, and return an unsubscribe.
   *
   * For DOM chrome that has to sit on top of the scene. Anchoring it from its
   * own `requestAnimationFrame` looked equivalent and was not: two callbacks
   * run in registration order, not in dependency order, so an overlay polling
   * on its own frame projected against whatever the camera was *before* this
   * loop advanced it — one frame stale, and only on the frames where the
   * ordering fell that way. A camera that moves smoothly and badges that lag
   * it by an inconsistent frame is read as both of them shaking.
   */
  onFrame(listener: () => void): () => void {
    this.frameListeners.add(listener);
    return () => {
      this.frameListeners.delete(listener);
    };
  }

  /**
   * Hear what the board does, and return an unsubscribe. Same shape as
   * `onFrame`, for the same reason: the caller is chrome, outside the scene.
   *
   * **What crosses is a visual moment, never a sound.** The renderer names
   * things it can see happening — a piece setting off, an oven lighting,
   * ground arriving — and knows nothing about the pack that plays them; the
   * mapping is `src/client/ai/ui/soundMap.ts`. That is what keeps a re-cut of the audio
   * out of the scene graph entirely.
   *
   * **The load-and-replay path is silent.** See `soundListeners`.
   */
  onSound(listener: SoundListener): () => void {
    this.soundListeners.add(listener);
    return () => {
      this.soundListeners.delete(listener);
    };
  }

  /**
   * Announce one moment.
   *
   * Not guarded on there being a listener: a `Set` of nought is a loop that
   * does not run, and a guard at every site is one more thing to get wrong at
   * the site that most matters.
   */
  private emitSound(event: SoundEvent): void {
    for (const listener of this.soundListeners) listener(event);
  }

  /**
   * The factory's beat is on screen: say what it did.
   *
   * Called from both release sites — staged behind the move, or with it under
   * "opponent plays fast" — so what is heard lines up with what is drawn
   * either way. The mint is deliberately *not* here any more: the forged piece
   * is drawn by `syncPieces` on the move's own frame, so holding its sound for
   * the beat read as the craft coming late rather than as sequencing.
   *
   * Emptied as it is spoken: a beat is announced once, and a snap that clears
   * `pendingIndustry` must not leave anything here for the next one.
   */
  private announceFactoryBeat(): void {
    const mined = this.pendingMined;
    this.pendingMined = 0;
    // **Non-positional, at -1.** The dig pass reports how much came up and
    // never which heaps it came off, so there is no square to put this at and a
    // centroid of the miners would be a claim the rules never made.
    if (mined > 0) this.emitSound({ kind: "collected", at: -1, amount: mined });
  }

  /**
   * Where the camera is, for anything positioning sound in the same space the
   * board is drawn in. The rig is private; this is the one way through.
   */
  cameraPose(): CameraPose {
    return this.rig.pose;
  }

  /**
   * Render on demand rather than continuously: a turn-based board is static
   * most of the time, and an idle tab should not spin the GPU.
   */
  private loop = (time: number): void => {
    this.frame = requestAnimationFrame(this.loop);

    // **Every frame is banked, drawn or not.** A skipped frame's time still
    // happened, and `nextCarry` keeps whatever is left over after a draw —
    // which is what makes a cap land on the number the player picked at any
    // display period rather than on the nearest divisor of it.
    if (this.frameInterval > 0) {
      this.banked += this.rawTime ? (time - this.rawTime) / 1000 : 0;
      this.rawTime = time;
      if (!frameDue(this.banked, this.frameInterval)) return;
      this.banked = nextCarry(this.banked, this.frameInterval);
    }

    // Seconds since the last frame that *drew*, which is what an animation has
    // to be advanced by. Clamped against the cap's own interval and not only
    // against `MAX_STEP`, or a slow cap would play the whole board slow.
    const raw = this.lastTime ? (time - this.lastTime) / 1000 : 0;
    const real = Math.min(stepClamp(this.frameInterval), raw);
    this.lastTime = time;

    // **What the clamp cost, kept so somebody can hear about it.** The gap
    // between `raw` and `real` is animation time the board did not get and the
    // driver's `setTimeout` counted anyway; unmeasured, the only symptom is
    // motion being cut off, which reads as the game speeding up. A frame over
    // `ASLEEP_FRAME` is a hidden tab rather than a slow one and says nothing
    // about how fast this machine draws, so it is left out of both sums.
    if (raw > 0 && raw <= ASLEEP_FRAME) {
      this.healthAdvanced += real;
      this.healthElapsed += raw;
      // A decaying average rather than a ring of samples: halving both keeps
      // the ratio and throws away the weight, so the reading follows a board
      // that has just got slower instead of averaging in the minute before it.
      if (this.healthElapsed > HEALTH_WINDOW) {
        this.healthAdvanced /= 2;
        this.healthElapsed /= 2;
      }
    }

    // **The whole frame, camera included.** The obvious reading is to speed up
    // the board and leave the camera alone — a camera is about the viewer's
    // eye rather than about whose turn it is — and it is wrong here for a
    // concrete reason: the view is *borrowed* to follow the move being played,
    // so a piece that arrives in a third of the time with the pan still
    // running lands off screen and the board catches up afterwards. One clock
    // keeps the piece, its factory, its guns and the look at it in step, which
    // is the property that actually matters.
    const dt = real * this.tempo;

    let busy = this.rig.update(dt);
    // **Whatever stands on a square rises with it.** The tiles own the offset
    // and everything else adds it, so a wave's army, its scrap and any lane it
    // brought come up welded to the new ground instead of hanging over the hole
    // waiting for a floor. Re-placed per frame because that offset changes per
    // frame; the tiles say when to stop asking.
    //
    // Nothing here for the change tint: it does not fade, so it costs the loop
    // no frames at all (§29 U4). It is written once by `setState` and stands
    // until the next change replaces it.
    if (this.tiles.update(dt)) {
      busy = true;
      this.pieces?.refreshGround();
      this.industry?.redraw();
      // **And nothing below the floor is drawn while it comes up.** The tiles
      // own the rise, so they own the answer; the army and the factory have no
      // way to work it out for themselves. Told every frame of the animation
      // and once more on the frame it settles — both `setRising` calls are
      // no-ops when the answer has not changed, which is what keeps this off
      // the ordinary frame's bill.
      const rising = this.tiles.rising();
      this.pieces?.setRising(rising);
      this.industry?.setRising(rising);
    }
    const moving = this.pieces?.update(dt) ?? false;
    if (moving) busy = true;
    // The hand is the player's, not the turn's: real time, whatever the tempo.
    if (this.carry?.motion.update(real)) busy = true;
    // **A piece's ingots travel with it.** The factory is deliberately a beat
    // behind the move (`pendingIndustry` below), so for the whole of an arc a
    // laden piece's holdings are still keyed to the square it left — they sat
    // there and reappeared in its hands when it landed, which read as dropping
    // the load and picking it up again. Re-placed per frame, the same way the
    // rise re-places everything standing on ground that is still coming up.
    if (moving && this.industry?.followMotion()) busy = true;

    // The factory's turn to move. Held back until the pieces have stopped, so
    // the two read as one thing after another rather than one blur.
    if (!moving && this.pendingIndustry) {
      const pending = this.pendingIndustry;
      this.pendingIndustry = undefined;
      this.industry?.sync(pending.facts, pending.cargo);
      // The beat is on screen now, which is the moment it is worth hearing —
      // a minted piece has just appeared.
      this.announceFactoryBeat();
      busy = true;
    }

    if (this.industry?.update(dt)) {
      busy = true;
      // **The army re-seats while the factory moves**, because a deck taking a
      // piece's weight is a spring now and `pieceLift` reads its live height.
      // Without this the piece hangs at the settled height while the belt
      // bounces underneath it — and the overshoot on the way back up puts the
      // deck through the model's base, which is the one frame of the effect
      // anybody would notice. Skips anything mid-move, like the rise does.
      this.pieces?.refreshGround();
    }
    this.busy = busy;

    if (busy || this.needsRender) {
      this.needsRender = busy;
      this.renderer.render(this.scene, this.rig.camera);
    }

    // Every frame, not only the ones that drew: a settled board renders
    // nothing, and a badge that appears on a settled board still has to be
    // placed. After the render, so anything projecting a square sees the same
    // camera the frame on screen was drawn with.
    for (const listener of this.frameListeners) listener();
  };
}

/**
 * How much bigger the direction picker is when it is aiming a belt part.
 *
 * One and a half: enough that the four arrows and the remove disc are each a
 * comfortable target with a piece and a pile of cargo sharing the square, and
 * not so much that the arrows reach past the neighbouring tile — they overhang
 * by design, but a control that spans three squares stops saying which one it
 * belongs to.
 */
const PICKER_ON_BELT = 1.5;

/**
 * How much of the viewport counts as "you can already see this".
 *
 * Eight tenths, so the outer tenth along each edge does not. A piece moving
 * inside that band is one the player is already looking at, and pulling the
 * board toward it is a shove rather than a lead — the exact complaint the
 * follow-the-piece toggle exists for, arriving on every turn the board happens
 * to be bigger than the viewport. A piece out in the margin is about to be
 * behind the header or the shelf, so it still earns a look.
 */
const COMFORTABLE = 0.8;

/** The height a lane is drawn at, as a plane to pick against. */
const BELT_PLANE = new Plane(new Vector3(0, 1, 0), -BELT_DECK);
/** The board's own plane. Unbounded, because the board is not. */
const GROUND_PLANE = new Plane(new Vector3(0, 1, 0), 0);
/** The height the pointer holds a carried body at. */
const GRIP_PLANE = new Plane(new Vector3(0, 1, 0), -CARRY_GRIP);

const CORNERS: [number, number][] = [
  [-0.5, -0.5],
  [0.5, -0.5],
  [-0.5, 0.5],
  [0.5, 0.5]
];

/**
 * How high a marker has to sit to clear a heap of `amount`.
 *
 * Mirrors the cone in `industryViews` — `0.12 + sqrt(n) * 0.13` — plus a hair.
 * Duplicated deliberately rather than exported: the two are the same number
 * for a reason that could stop being true (a heap could be redrawn as
 * something flatter), and a marker that clears it is a *requirement* rather
 * than a consequence.
 */
const heapMarkerHeight = (amount: number): number =>
  amount > 0 ? 0.12 + Math.sqrt(amount) * 0.13 + 0.04 : 0;

export { squareToWorld };
